import React, { useState, useEffect, useCallback, memo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Select } from "antd";
import { FieldWrapper } from "../FieldWrapper";
import {
  FieldComponentProps,
  SelectFieldConfig,
  FieldOption,
  FetchParameter,
} from "models/formField";
import { getFormData } from "services/form.service";
import { getFieldError } from "helpers/formField.components.helper";
import styles from "./SelectField.module.scss";

/**
 * SelectField - Dropdown/select component integrated with React Hook Form
 * Supports both static and async options
 */
export const SelectField: React.FC<FieldComponentProps<SelectFieldConfig>> =
  memo(
    ({
      config,
      control: controlProp,
      errors: errorsProp,
      watch: watchProp,
    }) => {
      // Get form context if available
      const formContext = useFormContext();
      const control = controlProp ?? formContext?.control;
      const errors = errorsProp ?? formContext?.formState?.errors;
      const watch = watchProp ?? formContext?.watch;

      const error = getFieldError(errors, config.name);
      const [options, setOptions] = useState<FieldOption[]>(
        config.options || [],
      );
      const [loading, setLoading] = useState(false);

      // Watch dependencies if they exist
      const watchedValues = config.fetch?.dependencies
        ? watch?.(config.fetch.dependencies)
        : null;

      /**
       * Fetch options from API
       */
      const fetchOptions = useCallback(
        async (formValues?: any) => {
          if (!config.fetch) return;

          setLoading(true);
          try {
            const {
              url,
              method = "get",
              params = [],
              formatter,
              serviceFn,
            } = config.fetch;

            let response: any;

            // Use serviceFn if provided, otherwise use url-based fetching
            if (serviceFn) {
              const serviceResponse = await serviceFn();
              // serviceFn returns axios response, so we need .data
              response = { data: serviceResponse?.data ?? serviceResponse };
            } else if (url) {
              let fetchUrl = url;
              let requestBody: any = {};

              if (method === "get") {
                // Build query string for GET requests
                const queryParts: string[] = [];
                params.forEach((param: FetchParameter) => {
                  let paramValue = param.value;
                  if (param.fieldReference && formValues) {
                    paramValue = formValues[param.fieldReference];
                    if (typeof paramValue === "object" && paramValue?.value) {
                      paramValue = paramValue.value;
                    }
                  }
                  queryParts.push(
                    `${encodeURIComponent(param.key)}=${encodeURIComponent(paramValue)}`,
                  );
                });

                if (queryParts.length > 0) {
                  fetchUrl = {
                    ...url,
                    url: `${url.url}?${queryParts.join("&")}`,
                  };
                }
              } else {
                // Build request body for POST requests
                params.forEach((param: FetchParameter) => {
                  let paramValue = param.value;
                  if (param.fieldReference && formValues) {
                    paramValue = formValues[param.fieldReference];
                    if (typeof paramValue === "object" && paramValue?.value) {
                      paramValue = paramValue.value;
                    }
                  }
                  requestBody[param.key] = paramValue;
                });
              }

              response = await getFormData(fetchUrl, method, requestBody);
            }

            // Debug log - remove in production
            if (process.env.NODE_ENV === "development") {
              console.log(
                `SelectField "${config.name}" API response:`,
                response?.data,
              );
            }

            const formattedOptions = formatter(response?.data || []);

            // Debug log - remove in production
            if (process.env.NODE_ENV === "development") {
              console.log(
                `SelectField "${config.name}" formatted options:`,
                formattedOptions,
              );
            }

            setOptions(formattedOptions);
            config.onOptionsLoaded?.(formattedOptions);
          } catch (err) {
            console.error("Error fetching options:", err);
            setOptions([]);
          } finally {
            setLoading(false);
          }
        },
        [config.fetch, config.onOptionsLoaded],
      );

      // Fetch options on mount if configured
      useEffect(() => {
        if (config.fetch && config.fetch.fetchOnMount !== false) {
          fetchOptions(watchedValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // Refetch when dependencies change
      useEffect(() => {
        if (config.fetch?.dependencies && watchedValues) {
          fetchOptions(watchedValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [JSON.stringify(watchedValues)]);

      // Update options when static options change
      useEffect(() => {
        if (config.options && !config.fetch) {
          setOptions(config.options);
        }
      }, [config.options, config.fetch]);

      if (!control) {
        console.error(
          `SelectField "${config.name}" requires control prop or FormProvider`,
        );
        return null;
      }

      return (
        <Controller
          name={config.name}
          control={control}
          defaultValue={config.defaultValue}
          rules={config.validation}
          render={({ field }) => (
            <FieldWrapper
              label={config.label}
              name={config.name}
              error={error}
              tooltip={config.tooltip}
              required={!!config.validation?.required}
              className={config.className}
              hidden={config.hidden}
            >
              <Select
                {...field}
                id={config.name}
                placeholder={config.placeholder}
                disabled={config.disabled || loading}
                loading={loading}
                allowClear={config.allowClear}
                showSearch={config.showSearch}
                className={`${styles.select} ${error ? styles.selectError : ""}`}
                aria-invalid={!!error}
                aria-describedby={error ? `${config.name}-error` : undefined}
                options={options.map((option) => ({
                  label: option.label,
                  value: option.value,
                  disabled: option.disabled,
                }))}
                onChange={(value) => {
                  field.onChange(value);
                  config.onChange?.(value);
                }}
                onBlur={() => {
                  field.onBlur();
                  config.onBlur?.();
                }}
                filterOption={(input, option) =>
                  String(option?.label || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                {...config.data}
              />
            </FieldWrapper>
          )}
        />
      );
    },
  );

SelectField.displayName = "SelectField";
